import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  GridLegacy as Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Rating,
  Stack,
  InputAdornment
} from '@mui/material';
import {
  Search,
  Person,
  Work,
  Schedule,
  CheckCircle,
  Cancel,
  Help,
  Visibility,
  CalendarToday,
  Business,
  Assessment,
  Clear,
  Refresh
} from '@mui/icons-material';
import { format } from 'date-fns';
import { adminAPI } from '../../services/api';

const InterviewSearch = () => {
  // State Management - Simplified
  const [searchParams, setSearchParams] = useState({
    candidate_name: ''
  });

  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Dialog states
  const [detailsDialog, setDetailsDialog] = useState({ open: false, interview: null });

  // Load initial data - all interviews sorted by recent first
  const performInitialSearch = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const searchData = {
        page: 1,
        per_page: 100,
        sort_by: 'scheduled_at',
        sort_order: 'desc' // Recent to oldest
      };

      const response = await adminAPI.searchInterviews(searchData);
      if (response.data.status === 'success') {
        setResults(response.data.interviews || []);
        setInitialLoadComplete(true);
      } else {
        setError(response.data.error || 'Failed to load interviews');
      }
    } catch (error) {
      console.error('Initial search error:', error);
      setError('Failed to load interviews. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Perform search by name
  const performSearch = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const searchData = {
        candidate_name: searchParams.candidate_name,
        page: 1,
        per_page: 100,
        sort_by: 'scheduled_at',
        sort_order: 'desc' // Recent to oldest
      };

      const response = await adminAPI.searchInterviews(searchData);
      if (response.data.status === 'success') {
        setResults(response.data.interviews || []);
      } else {
        setError(response.data.error || 'Search failed');
      }
    } catch (error) {
      console.error('Search error:', error);
      setError('Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [searchParams]);

  // Load data on mount
  useEffect(() => {
    performInitialSearch();
  }, [performInitialSearch]);

  // Handle input changes
  const handleInputChange = (field, value) => {
    setSearchParams(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchParams.candidate_name.trim()) {
      performSearch();
    } else {
      performInitialSearch(); // Show all if search is empty
    }
  };

  // Clear search
  const clearSearch = () => {
    setSearchParams({ candidate_name: '' });
    setError('');
    performInitialSearch();
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'scheduled': return 'info';
      case 'cancelled': return 'error';
      case 'rescheduled': return 'warning';
      default: return 'default';
    }
  };

  // Get verdict color and icon
  const getVerdictDisplay = (verdict) => {
    switch (verdict) {
      case 'hire':
        return { color: 'success', icon: <CheckCircle />, label: 'Hire' };
      case 'reject':
        return { color: 'error', icon: <Cancel />, label: 'Reject' };
      case 'maybe':
        return { color: 'warning', icon: <Help />, label: 'Maybe' };
      default:
        return { color: 'default', icon: <Help />, label: verdict || 'Pending' };
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
    } catch {
      return 'Invalid Date';
    }
  };

  // Handle view details
  const handleViewDetails = async (interview) => {
    try {
      const response = await adminAPI.getInterviewDetails(interview.id);
      setDetailsDialog({ open: true, interview: response.data.interview });
    } catch (error) {
      console.error('Failed to load interview details:', error);
      setDetailsDialog({ open: true, interview }); // Fallback to basic data
    }
  };

  // Interview Card Component - Using actual data structure
  const InterviewCard = ({ interview }) => {
    const verdict = getVerdictDisplay(interview.final_verdict);
    const statusColor = getStatusColor(interview.status);

    return (
      <Card 
        elevation={3}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: 6
          },
          transition: 'all 0.3s ease'
        }}
      >
        <CardContent sx={{ flexGrow: 1 }}>
          {/* Header */}
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Typography variant="h6" noWrap>
              {interview.candidate_name}
            </Typography>
            <Box display="flex" gap={1}>
              <Chip 
                label={interview.status} 
                color={statusColor}
                size="small"
              />
              {interview.final_verdict && (
                <Chip
                  icon={verdict.icon}
                  label={verdict.label}
                  color={verdict.color}
                  size="small"
                />
              )}
            </Box>
          </Box>

          {/* Job & Basic Info */}
          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
            {interview.job_title}
          </Typography>
          
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Schedule />
            <Typography variant="body2">
              {formatDate(interview.scheduled_at)}
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Person />
            <Typography variant="body2">
              Panelist: {interview.panelist?.name || 'N/A'}
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <Business />
            <Typography variant="body2">
              Vendor: {interview.vendor?.name || 'N/A'}
            </Typography>
          </Box>

          {/* Ratings - only if available */}
          {interview.ratings?.overall > 0 && (
            <Box mb={2}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Overall Rating:
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <Rating value={interview.ratings.overall} readOnly size="small" />
                <Typography variant="body2">
                  ({interview.ratings.overall}/5)
                </Typography>
              </Box>
            </Box>
          )}

          {/* Feedback Preview - only if available */}
          {interview.feedback?.notes && (
            <Box>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Feedback:
              </Typography>
              <Typography 
                variant="body2" 
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical'
                }}
              >
                {interview.feedback.notes}
              </Typography>
            </Box>
          )}
        </CardContent>

        <CardActions>
          <Button
            size="small"
            startIcon={<Visibility />}
            onClick={() => handleViewDetails(interview)}
          >
            View Details
          </Button>
        </CardActions>
      </Card>
    );
  };

  return (
    <Container maxWidth="xl">
      {/* Header */}
      <Box mb={3}>
        <Typography variant="h4" gutterBottom>
          🎯 Interview Search
        </Typography>
        <Typography variant="subtitle1" color="textSecondary">
          Search interviews by candidate name (sorted recent to oldest)
        </Typography>
      </Box>

      {/* Simple Search Form */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Search Interviews
        </Typography>
        
        <form onSubmit={handleSearch}>
          <Grid container spacing={3} alignItems="center">
            {/* Candidate Name Search */}
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Search by Candidate Name"
                value={searchParams.candidate_name}
                onChange={(e) => handleInputChange('candidate_name', e.target.value)}
                placeholder="Enter candidate name..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={2}>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={20} /> : <Search />}
                  disabled={loading}
                  sx={{
                    '&:hover': {
                    }
                  }}
                >
                  {loading ? 'Searching...' : 'Search'}
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<Clear />}
                  onClick={clearSearch}
                >
                  Clear
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={performInitialSearch}
                >
                  Refresh
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Results */}
      <Box>
        <Typography variant="h6" gutterBottom>
          {searchParams.candidate_name 
            ? `Search Results for "${searchParams.candidate_name}" (${results.length} found)`
            : `All Interviews (${results.length} total, sorted recent to oldest)`
          }
        </Typography>

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : results.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="textSecondary">
              {searchParams.candidate_name 
                ? `No interviews found for "${searchParams.candidate_name}"`
                : 'No interviews found'
              }
            </Typography>
            <Typography color="textSecondary">
              {searchParams.candidate_name 
                ? 'Try a different name or clear the search to see all interviews'
                : 'No interview data available in the system'
              }
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {results.map((interview) => (
              <Grid item xs={12} md={6} lg={4} key={interview.id}>
                <InterviewCard interview={interview} />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Interview Details Dialog */}
      <Dialog
        open={detailsDialog.open}
        onClose={() => setDetailsDialog({ open: false, interview: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Interview Details: {detailsDialog.interview?.candidate_name}
        </DialogTitle>
        <DialogContent>
          {detailsDialog.interview && (
            <Box>
              {/* Candidate Info */}
              <Typography variant="h6" gutterBottom>
                👤 Candidate Information
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><Person /></ListItemIcon>
                  <ListItemText 
                    primary={detailsDialog.interview.candidate_name}
                    secondary={detailsDialog.interview.candidate_email}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><Work /></ListItemIcon>
                  <ListItemText 
                    primary={detailsDialog.interview.job_title}
                    secondary={`Job ID: ${detailsDialog.interview.job_id}`}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CalendarToday /></ListItemIcon>
                  <ListItemText 
                    primary="Interview Scheduled"
                    secondary={formatDate(detailsDialog.interview.scheduled_at)}
                  />
                </ListItem>
                {detailsDialog.interview.completed_at && (
                  <ListItem>
                    <ListItemIcon><CheckCircle /></ListItemIcon>
                    <ListItemText 
                      primary="Completed"
                      secondary={formatDate(detailsDialog.interview.completed_at)}
                    />
                  </ListItem>
                )}
              </List>

              {/* Ratings - only if available */}
              {detailsDialog.interview.ratings?.overall > 0 && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    📊 Interview Ratings
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          Overall Rating
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Rating 
                            value={detailsDialog.interview.ratings.overall} 
                            readOnly 
                            size="small" 
                          />
                          <Typography variant="body2">
                            ({detailsDialog.interview.ratings.overall}/5)
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    {detailsDialog.interview.ratings.technical > 0 && (
                      <Grid item xs={6}>
                        <Box>
                          <Typography variant="body2" color="textSecondary">
                            Technical Rating
                          </Typography>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Rating 
                              value={detailsDialog.interview.ratings.technical} 
                              readOnly 
                              size="small" 
                            />
                            <Typography variant="body2">
                              ({detailsDialog.interview.ratings.technical}/5)
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </>
              )}

              {/* Feedback - only if available */}
              {detailsDialog.interview.feedback?.notes && (
                <>
                  <Typography variant="h6" gutterBottom>
                    💬 Interview Feedback
                  </Typography>
                  <Paper sx={{ p: 2, mb: 2, backgroundColor: 'grey.50' }}>
                    <Typography variant="body2">
                      {detailsDialog.interview.feedback.notes}
                    </Typography>
                  </Paper>

                  {detailsDialog.interview.feedback.strengths && (
                    <Box mb={2}>
                      <Typography variant="subtitle2" color="success.main" gutterBottom>
                        ✅ Strengths
                      </Typography>
                      <Typography variant="body2">
                        {detailsDialog.interview.feedback.strengths}
                      </Typography>
                    </Box>
                  )}

                  {detailsDialog.interview.feedback.areas_for_improvement && (
                    <Box mb={2}>
                      <Typography variant="subtitle2" color="warning.main" gutterBottom>
                        📈 Areas for Improvement
                      </Typography>
                      <Typography variant="body2">
                        {detailsDialog.interview.feedback.areas_for_improvement}
                      </Typography>
                    </Box>
                  )}

                  {detailsDialog.interview.feedback.recommendations && (
                    <Box>
                      <Typography variant="subtitle2" color="info.main" gutterBottom>
                        💡 Recommendations
                      </Typography>
                      <Typography variant="body2">
                        {detailsDialog.interview.feedback.recommendations}
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialog({ open: false, interview: null })}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default InterviewSearch;
          